import {
    LEGAL_FORM,
    REPORTING_PERIOD,
    SALARY_PACKAGE,
} from '@/helpers/constants';
import { apiDate } from '@/platform/lib/utils';
import {
    Button,
    Container,
    Dropdown,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Segment,
    SegmentContent,
    SegmentHeader,
    Toggle,
} from '@/platform/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import { useEffect, useMemo } from 'react';

const CreateOrUpdateView = () => {
    const { auth, entity, contacts } = usePage().props;
    const { data, setData, post, patch, errors, processing } = useForm({});

    useEffect(() => {
        setData(entity || {});
    }, [entity]);

    const title = useMemo(
        () => (entity?.id ? 'Wijzig entiteit' : 'Nieuwe entiteit'),
        [entity?.id]
    );

    const handleSubmit = () => {
        if (entity?.id) {
            patch(`/customers/${entity?.customer_id}/entities/${entity.id}`);

            return;
        }

        post(`/customers/${auth?.user?.selected_customer_id}/entities`);
    };

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader header={title}>
                        <Button
                            as={Link}
                            href={`/customers/${auth?.user?.selected_customer_id}/entities`}
                        >
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <FormGroup>
                            {entity?.id && (
                                <Toggle
                                    label='Is actief'
                                    checked={data?.status === 1}
                                    onChange={checked =>
                                        setData('status', checked ? 1 : 0)
                                    }
                                    error={errors?.status}
                                />
                            )}
                            <Toggle
                                label='Test entiteit'
                                checked={!!data?.is_test || false}
                                onChange={checked =>
                                    setData('is_test', checked ? 1 : 0)
                                }
                                error={errors?.is_test}
                            />
                            <Input
                                required
                                label='Organisatienaam'
                                value={data?.name || ''}
                                onChange={event =>
                                    setData('name', event.target.value)
                                }
                                error={errors?.name}
                            />
                            <Input
                                required
                                disabled={!!entity?.datahub_id}
                                label='Loonheffingennummer'
                                value={data?.loonheffingennummer || ''}
                                onChange={event =>
                                    setData(
                                        'loonheffingennummer',
                                        event.target.value
                                    )
                                }
                                error={errors?.loonheffingennummer}
                            />
                            <Input
                                label='Afzender email prefix'
                                value={data?.email_prefix || ''}
                                onChange={event =>
                                    setData('email_prefix', event.target.value)
                                }
                                error={errors?.email_prefix}
                                suffix='@vcsw.nl'
                            />
                            <Input
                                required
                                disabled={!!entity?.datahub_id}
                                label='KvK nummer'
                                value={data?.kvk || ''}
                                onChange={event =>
                                    setData('kvk', event.target.value)
                                }
                                error={errors?.kvk}
                            />
                        </FormGroup>
                        <FormGroup inline equal>
                            <Input
                                label='Startdatum'
                                type='date'
                                value={apiDate(data?.startup_at) || ''}
                                onChange={event =>
                                    setData('startup_at', event.target.value)
                                }
                                error={errors?.startup_at}
                            />
                            <Input
                                label='Einddatum'
                                type='date'
                                value={apiDate(data?.end_at) || ''}
                                onChange={event =>
                                    setData('end_at', event.target.value)
                                }
                                error={errors?.end_at}
                            />
                        </FormGroup>
                        <FormGroup inline equal>
                            <Dropdown
                                label='Rechtsvorm'
                                placeholder='Selecteer..'
                                options={Object.keys(LEGAL_FORM).map(
                                    option => ({
                                        id: option,
                                        name: option,
                                    })
                                )}
                                value={data?.legal_form || ''}
                                onChange={value => setData('legal_form', value)}
                                error={errors?.legal_form}
                            />
                            <Dropdown
                                label='Salarispakket'
                                disabled={!!entity?.datahub_id}
                                placeholder='Selecteer..'
                                options={Object.keys(SALARY_PACKAGE).map(
                                    option => ({
                                        id: option,
                                        name: option,
                                    })
                                )}
                                value={data?.salary_package || ''}
                                onChange={value =>
                                    setData('salary_package', value)
                                }
                                error={errors?.salary_package}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                label='E-mailadres facturatie'
                                description='Optioneel. Indien leeg, wordt het facturatie e-mailadres van de klant gebruikt.'
                                type='email'
                                value={data?.invoice_email || ''}
                                onChange={event =>
                                    setData('invoice_email', event.target.value)
                                }
                                error={errors?.invoice_email}
                            />
                            <Input
                                label='E-mailadres geregistreerd'
                                description='Optioneel. E-mailadres opgehaald uit Datahub.'
                                type='email'
                                value={data?.registered_email || ''}
                                onChange={event =>
                                    setData(
                                        'registered_email',
                                        event.target.value
                                    )
                                }
                                error={errors?.registered_email}
                            />
                            <Dropdown
                                label='Aangifteperiode'
                                placeholder='Selecteer..'
                                disabled={!!entity?.datahub_id}
                                options={Object.keys(REPORTING_PERIOD).map(
                                    option => ({
                                        id: option,
                                        name: option,
                                    })
                                )}
                                value={data?.reporting_period || ''}
                                onChange={value =>
                                    setData('reporting_period', value)
                                }
                                error={errors?.reporting_period}
                            />
                            <Deferred
                                data='contacts'
                                fallback={
                                    <InputPlaceholder label='Contactpersoon UWV Digipoort' />
                                }
                            >
                                <Dropdown
                                    label='Contactpersoon UWV Digipoort'
                                    placeholder='Selecteer..'
                                    options={contacts || ''}
                                    value={data?.digipoort_contact_id || ''}
                                    onChange={value =>
                                        setData('digipoort_contact_id', value)
                                    }
                                    error={errors?.digipoort_contact_id}
                                />
                            </Deferred>
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <Segment className='not-last:mb-0'>
                        <SegmentHeader
                            header='Factuuradres'
                            subheader='Dit adres wordt niet beïnvloed door updates van derden.'
                        />
                        <SegmentContent>
                            <FormGroup>
                                <Input
                                    label='Straatnaam'
                                    required
                                    value={data?.invoice_address?.street || ''}
                                    onChange={event =>
                                        setData(
                                            'invoice_address.street',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.['invoice_address.street']}
                                />
                                <Input
                                    label='Huisnummer'
                                    required
                                    value={
                                        data?.invoice_address?.house_number ||
                                        ''
                                    }
                                    onChange={event =>
                                        setData(
                                            'invoice_address.house_number',
                                            event.target.value
                                        )
                                    }
                                    error={
                                        errors?.['invoice_address.house_number']
                                    }
                                />
                                <Input
                                    label='Postcode'
                                    required
                                    value={data?.invoice_address?.zipcode || ''}
                                    onChange={event =>
                                        setData(
                                            'invoice_address.zipcode',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.['invoice_address.zipcode']}
                                />
                                <Input
                                    label='Plaats'
                                    required
                                    value={data?.invoice_address?.city || ''}
                                    onChange={event =>
                                        setData(
                                            'invoice_address.city',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.['invoice_address.city']}
                                />
                                <Input
                                    label='Land'
                                    value={data?.invoice_address?.country || ''}
                                    onChange={event =>
                                        setData(
                                            'invoice_address.country',
                                            event.target.value
                                        )
                                    }
                                    error={errors?.['invoice_address.country']}
                                />
                            </FormGroup>
                        </SegmentContent>
                    </Segment>

                    {entity?.registered_address && (
                        <Segment>
                            <SegmentHeader
                                header='Geregistreerd adres'
                                subheader='Dit adres wordt bijgewerkt tijdens de datahubsynchronisatie.'
                            />
                            <SegmentContent>
                                <FormGroup>
                                    <Input
                                        label='Straatnaam'
                                        value={
                                            data?.registered_address?.street ||
                                            ''
                                        }
                                        onChange={event =>
                                            setData(
                                                'registered_address.street',
                                                event.target.value
                                            )
                                        }
                                        disabled
                                    />
                                    <Input
                                        label='Huisnummer'
                                        value={
                                            data?.registered_address
                                                ?.house_number || ''
                                        }
                                        disabled
                                    />
                                    <Input
                                        label='Postcode'
                                        value={
                                            data?.registered_address?.zipcode ||
                                            ''
                                        }
                                        disabled
                                    />
                                    <Input
                                        label='Plaats'
                                        value={
                                            data?.registered_address?.city || ''
                                        }
                                        disabled
                                    />
                                    <Input
                                        label='Land'
                                        value={
                                            data?.registered_address?.country ||
                                            ''
                                        }
                                        disabled
                                    />
                                </FormGroup>
                            </SegmentContent>
                        </Segment>
                    )}
                </div>

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
